import * as React from 'react';
import { MouseEvent } from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material';
import ActionDelete from '@mui/icons-material/Delete';
import clsx from 'clsx';
import {
    Button,
    DeleteWithUndoButtonProps,
    useRecordContext,
    useResourceContext,
} from 'react-admin';
import { useDeleteBranchWithUndoController } from './controllers';
import { TreeRecord } from './types';

export const DeleteBranchWithUndoButton = <
    RecordType extends TreeRecord = TreeRecord
>(
    props: DeleteWithUndoButtonProps<RecordType>
) => {
    const {
        label = 'ra.action.delete',
        className,
        icon = defaultIcon,
        onClick,
        resource: resourceProp,
        record: recordProp,
        redirect = 'list',
        ...rest
    } = props;
    const resource = useResourceContext(props);
    const record = useRecordContext(props);
    const { handleDelete, isLoading } =
        useDeleteBranchWithUndoController<RecordType>({
            resource,
            record,
            redirect,
        });

    const handleClick = (event: MouseEvent) => {
        if (onClick) {
            onClick(event);
        }

        if (!event.isDefaultPrevented()) {
            handleDelete(event);
        }
    };

    return (
        <Root
            onClick={handleClick}
            disabled={isLoading}
            label={label}
            className={clsx('ra-delete-button', className)}
            key="button"
            {...rest}
        >
            {icon}
        </Root>
    );
};

const Root = styled(Button, {
    name: 'RaDeleteBranchWithUndoButton',
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    color: theme.palette.error.main,
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        '@media (hover: none)': {
            backgroundColor: 'transparent',
        },
    },
}));

const defaultIcon = <ActionDelete />;

DeleteBranchWithUndoButton.propTypes = {
    className: PropTypes.string,
    label: PropTypes.string,
    record: PropTypes.any,
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func,
    ]),
    resource: PropTypes.string,
    icon: PropTypes.element,
};
